import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { canEditOpenCanvas, pickedSelectors, roleOf, useStore } from '../lib/store'
import {
  MOD_KEY,
  PRESENT_SHORTCUT_HINT,
  isControlTarget,
  isHideUiShortcut,
  isPresentShortcut,
  isSelectAllShortcut,
  isTypingTarget,
  reorderShortcut,
} from '../lib/keys'
import { enterSelection, exitSelection, reorderSelection, selectAll, selectSibling } from '../lib/selectionKeys'
import { presentStartFrame } from '../lib/present'
import { canHideUi, hideUiHinted } from '../lib/hideUi'
import { connect, disconnect, sendWs } from '../lib/ws'
import {
  api,
  ApiError,
  type DiscoveredSite,
  type GithubConnectionInfo,
  type InstallationRepo,
  type RepoManifest,
  type RepoScreen,
  type SyncKeyInfo,
} from '../lib/api'
import { navigate } from '../App'
import { DoopMark } from '../components/Logo'
import {
  BarDivider,
  BarPill,
  TopBar,
  TopBarTitle,
  barIconBtn,
  barPillBtn,
  barPillIconBtn,
  barPrimaryBtn,
  barPrimaryCompactBtn,
} from '../components/TopBar'
import { LivePresence } from '../components/LivePresence'
import { ensureTab } from '../lib/desktop'
import { Stage } from '../components/Stage'
import { Inspector } from '../components/Inspector'
import { PagePanel } from '../components/PagePanel'
import { ElementPanel } from '../components/ElementPanel'
import { ActivityPanel } from '../components/ActivityPanel'
import { ConnectModal } from '../components/ConnectModal'
import { LimitWall, isResidentLimit } from '../components/TeamAllowance'
import { WorkingNow } from '../components/WorkingNow'
import { MobilePanelTabs, SideRail } from '../components/SideRail'
import { LayersPanel, LayersRailToggle } from '../components/LayersPanel'
import { Onboarding } from '../components/Onboarding'
import { ShareModal } from '../components/ShareModal'
import { ExportModal } from '../components/ExportModal'
import { pageFrames } from '../../shared/pages'
import type { Frame } from '../../shared/types'
import { ImportStepFooter, ImportStepHeader, SourcePicker } from '../components/ImportSources'
import { importSources } from '../components/importSourceList'
import { PresentMode } from '../components/PresentMode'
import { getIdentity, setName } from '../lib/identity'
import {
  copyFrames,
  dropFiles,
  duplicateFrames,
  hasFrameClip,
  hasIncoming,
  pasteFrameCentered,
  pasteFilesCentered,
  splitIncoming,
  svgMarkupOf,
} from '../lib/frameClipboard'
import { clearHistory, redo, undo } from '../lib/history'
import {
  deleteSelection,
  duplicateLayers,
  nudgeSelection,
  toggleLockSelection,
  toggleSelectionHidden,
  formatSelectionText,
} from '../lib/layerEdits'
import type { TextFormatCommand } from '../lib/richText'
import { copyPicked, hasElementClip, pasteLayer } from '../lib/elementClipboard'
import { isCanvasClipboard, writeCanvasClipboard } from '../lib/canvasClipboard'
import { authClient } from '../lib/auth'
import { avatarUrl } from '../../shared/avatar'
import { posthog } from '../lib/posthog'
import { useIsMobile } from '../hooks/use-mobile'
import { cn } from '@/lib/utils'
import { Button } from '../components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet'
import { AuthCard, AuthCardText, AuthScreen } from '../components/ui/screen'
import {
  ArrowLeftIcon,
  BrainIcon,
  CopyIcon,
  ExportIcon,
  FolderIcon,
  GearIcon,
  GithubIcon,
  ImportIcon,
  LayersIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PlayIcon,
  PlusIcon,
  PrototypeModeIcon,
  RadioTowerIcon,
  ShareIcon,
  ShareUpIcon,
  WebIcon,
  XIcon,
} from '../components/ui/icons'
import { Badge, badgeVariants } from '../components/ui/badge'
import { Pressable } from '../components/ui/pressable'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { SelectionBar, SelectionBarAction } from '../components/ui/selection-bar'
import { Input } from '../components/ui/input'
import { Field } from '../components/ui/field'
import { PeerAvatars, PresenceFace } from '../components/PeerAvatars'
import { Checkbox, CheckboxCard } from '../components/ui/checkbox'
import { Toast, ToastAction, ToastSpinner } from '../components/ui/toast'
import { Tooltip } from '../components/ui/tooltip'
import { Note } from '../components/ui/note'
import { Textarea } from '../components/ui/textarea'
import { Modal, ModalActions, ModalEyebrow, ModalLede, ModalTitle } from '../components/ui/modal'
import { createCenteredFrame } from '../lib/framePlacement'
import { RULER_PX } from '../components/Rulers'
import { NO_CANVAS, onViewCommand, syncViewMenu } from '../lib/viewMenu'
import { AGENTS_MIN, LAYERS_MIN, fitPanelWidths } from '../lib/panelWidths'
import { loadCanvasFonts } from '../lib/customFonts'
import { t as translate, useT } from '../lib/i18n'
import { NO_INSETS, type StageInsets } from '../lib/viewFit'
import { usePanelLayout } from '../lib/panelLayout'

/* The floating chrome's geometry, in px from the canvas area's edges. The
   panels sit 14px in from the edge; on the right the resizable Agents panel
   always hugs the edge, the Inspector (244) opens to its left, and the icon
   islands (40) sit left of whatever is open. On the left the tool rail follows the Layers
   panel, or its collapsed 40px island. --chrome-left/--chrome-right
   mark where the open canvas begins and ends, for the zoom pill and toasts.
   With rulers on, the chrome layer starts RULER_PX further in on the top
   and left; --chrome-top and --rail-left include that, these offsets do not.
   Docked (Settings → Appearance → Canvas panels), the same pieces are
   columns flush with each other and the window's edges, and the stage
   itself fills the gap between them, rulers and all. */
const EDGE = 14
const ARROW_NUDGE: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
}
const LETTER_FORMATS: Record<string, TextFormatCommand> = { b: 'bold', i: 'italic', u: 'underline' }

/** The text format a canvas key chord names: letters without ⇧, the ⇧ digits by code (⇧7 is & on US keyboards). */
function layerTextShortcut(e: KeyboardEvent): TextFormatCommand | null {
  if (!e.shiftKey) return LETTER_FORMATS[e.key.toLowerCase()] ?? null
  if (e.key.toLowerCase() === 'x') return 'strikeThrough'
  return e.code === 'Digit7' ? 'insertOrderedList' : e.code === 'Digit8' ? 'insertUnorderedList' : null
}
const LAYERS_W = 264
const RAIL_W = 40
const INSPECTOR_W = 244
/* the bar's height on desktop */
const BAR_H = 50
/* the docked bar is a flat strip, so it can sit a little tighter */
const DOCKED_BAR_H = 44
/* how far what floats over the canvas keeps from a docked column */
const FLOAT_GAP = 10
/* the narrowest stage docked panels squeeze it to, while their minimums allow */
const DOCKED_STAGE_MIN = 320
const AGENTS_W = 280
const PANEL_WIDTH_KEY = 'doop:canvas-panel-widths'

function readPanelWidths(): { layers: number; agents: number } {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(PANEL_WIDTH_KEY) ?? 'null')
    if (saved && typeof saved === 'object' && 'layers' in saved && 'agents' in saved) {
      return {
        layers: typeof saved.layers === 'number' && Number.isFinite(saved.layers) ? saved.layers : LAYERS_W,
        agents: typeof saved.agents === 'number' && Number.isFinite(saved.agents) ? saved.agents : AGENTS_W,
      }
    }
  } catch {
    /* Unavailable or invalid storage must not prevent opening a canvas. */
  }
  return { layers: LAYERS_W, agents: AGENTS_W }
}

function chromeLayout({
  layersOpen,
  propertiesShown,
  agentsOpen,
  layersWidth,
  agentsWidth,
  docked,
}: {
  layersOpen: boolean
  propertiesShown: boolean
  agentsOpen: boolean
  layersWidth: number
  agentsWidth: number
  docked: boolean
}) {
  if (docked) {
    const toolRail = layersOpen ? layersWidth : RAIL_W
    const properties = agentsOpen ? agentsWidth : 0
    const island = propertiesShown ? properties + INSPECTOR_W : properties
    return { toolRail, properties, island, left: toolRail + RAIL_W, right: island + RAIL_W }
  }
  const toolRail = layersOpen ? EDGE + layersWidth + 12 : EDGE + RAIL_W + 10
  const properties = agentsOpen ? EDGE + agentsWidth + 8 : EDGE
  const island = propertiesShown ? properties + INSPECTOR_W + 10 : agentsOpen ? EDGE + agentsWidth + 10 : EDGE
  return { toolRail, properties, island, left: toolRail + RAIL_W + 16, right: island + RAIL_W + 10 }
}

const STARTER_HTML = `<!doctype html>
<html>
<head>
<style>
  * { margin: 0; box-sizing: border-box; }
  body {
    font-family: system-ui, sans-serif;
    height: 100vh;
    display: grid;
    place-items: center;
    background: #fafafa;
    color: #999;
  }
</style>
</head>
<body>
  <p>Design me — edit the HTML, or ask an agent.</p>
</body>
</html>`

/* Small captions the import flow repeats under its fields. */
const importNoteCls = 'mt-2.5 text-[11.5px] leading-[1.4] text-ink-faint'
const errorNoteCls = 'mt-2.5 text-[13px] text-accent-ink'

export function CanvasPage({ canvasId }: { canvasId: string }) {
  const t = useT()
  const canvas = useStore((s) => s.canvas)
  const connected = useStore((s) => s.connected)
  const presences = useStore((s) => s.presences)
  const selectedId = useStore((s) => s.selectedId)
  const select = useStore((s) => s.select)
  /* a view or comment share link: the canvas opens read-only — no imports,
     no property panels, no edit shortcuts. The server refuses those writes
     regardless; this keeps the UI from offering them. */
  const role = useStore(roleOf)
  const readOnly = useStore((s) => !canEditOpenCanvas(s))
  const isMobile = useIsMobile()
  const prototypeMode = useStore((s) => s.prototypeMode)
  const [panelWidths, setPanelWidths] = useState(readPanelWidths)
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth)
  useEffect(() => {
    const updateWidth = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])
  useEffect(() => {
    try {
      localStorage.setItem(PANEL_WIDTH_KEY, JSON.stringify(panelWidths))
    } catch {
      /* Resizing still works when preferences cannot be saved. */
    }
  }, [panelWidths])
  const [showActivity, setShowActivity] = useState(() => !window.matchMedia('(max-width: 900px)').matches)
  /* ?connect=1 (the "connect your agent" email) lands on the Connect AI
     modal; the flag comes off the URL once the page is up so a reload or a
     copied link does not reopen it */
  const [showConnect, setShowConnect] = useState(() => new URLSearchParams(location.search).get('connect') === '1')
  useEffect(() => {
    const url = new URL(location.href)
    if (url.searchParams.get('connect') !== '1') return
    url.searchParams.delete('connect')
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])
  const [showShare, setShowShare] = useState(false)
  /* the frame the player was launched on, while presenting */
  const [presenting, setPresenting] = useState<string | null>(null)
  /* ⌘. acts only over the canvas view, outside PresentMode */
  const hideUiActive = canHideUi({ view: 'canvas', presenting: !!presenting })
  const activePageId = useStore((s) => s.activePageId)
  /* Play starts on the selected frame, or on the page's flow start */
  const playFrom = useMemo(
    () => presentStartFrame(canvas, selectedId, activePageId),
    [selectedId, canvas, activePageId],
  )
  /* returning from a GitHub App install: the setup redirect appends a signed
     pass — pull it off the URL and open the import modal on the repo picker */
  const [ghInstallPass, setGhInstallPass] = useState<string | null>(() => {
    const pass = new URLSearchParams(location.search).get('ghInstall')
    if (pass) history.replaceState(null, '', location.pathname)
    return pass
  })
  /* a failed install round-trip also lands here, with the reason to show */
  const [ghInstallError] = useState<string | null>(() => {
    const err = new URLSearchParams(location.search).get('ghError')
    if (err) history.replaceState(null, '', location.pathname)
    return err
  })
  /* ?import=<source> (Home's Import picker, on the canvas it just made) opens
     the import modal on that source's screen (`1` for the picker itself); the
     flag comes off the URL once the page is up, the same way ?connect=1 does —
     stripping it in the initializer loses it to the remount that follows the
     canvas load */
  const [importRequested] = useState(() => new URLSearchParams(location.search).get('import'))
  useEffect(() => {
    const url = new URL(location.href)
    if (!url.searchParams.has('import')) return
    url.searchParams.delete('import')
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])
  const [showImport, setShowImport] = useState(!!ghInstallPass || !!ghInstallError || !!importRequested)
  const [exporting, setExporting] = useState<{ frames: Frame[]; selected: string[] } | null>(null)
  const [showMobileActions, setShowMobileActions] = useState(false)
  const [showMobileLayers, setShowMobileLayers] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const storeNotice = useStore((s) => s.notice)
  const updateReady = useStore((s) => s.updateReady)
  const limitWall = useStore((s) => s.limitWall)
  const canvasNotFound = useStore((s) => s.canvasNotFound)

  /* keep the desktop shell's tab label in step with the live canvas name.
     The store's canvas briefly lags a navigation (the previous page's data
     until this one's ws init lands), so only sync when it's really ours —
     otherwise closing a tab re-adds it from its own stale state. */
  useEffect(() => {
    if (canvas?.id === canvasId) ensureTab(canvas.id, canvas.name)
  }, [canvas, canvasId])

  useEffect(() => {
    connect(canvasId)
    return () => {
      disconnect()
      useStore.getState().setCanvas(null)
      useStore.getState().setPresences([])
      useStore.getState().setCanvasNotFound(false)
      select(null)
      useStore.getState().setTool('select')
      clearHistory()
    }
  }, [canvasId, select])

  /* the font library, again when the canvas moves between workspaces (its
     library is the workspace's) and on return to the tab, so a font
     uploaded in Settings in another tab shows up without a reload */
  const loadedCanvasId = canvas?.id === canvasId ? canvasId : null
  const fontScope = canvas?.workspaceId ?? null
  useEffect(() => {
    if (!loadedCanvasId) return
    loadCanvasFonts(loadedCanvasId)
    const refresh = () => {
      if (document.visibilityState === 'visible') loadCanvasFonts(loadedCanvasId)
    }
    document.addEventListener('visibilitychange', refresh)
    return () => document.removeEventListener('visibilitychange', refresh)
  }, [loadedCanvasId, fontScope])

  /* broadcast which frame I'm focused on */
  useEffect(() => {
    sendWs({ type: 'editing', frameId: selectedId })
  }, [selectedId])

  /* frame keyboard shortcuts: delete, copy/paste/duplicate, undo/redo */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      /* ⌘K from anywhere, fields included: open the side panel on the Agents
         tab and put the keyboard in its "Design it" composer. Asking for the
         composer ends ⌘. hide-UI — a field under `hidden` cannot take focus. */
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setShowActivity(true)
        if (useStore.getState().uiHidden) useStore.getState().toggleUiHidden()
        useStore.getState().setPanelTab('tasks')
        useStore.getState().requestComposerFocus()
        return
      }
      if (isTypingTarget(e.target)) return
      const selectedIds = useStore.getState().selectedIds
      /* a picked element (from the canvas or the layers tree) is what ⌫
         removes — the frame it lives in only goes when nothing inside it is
         selected. Auto-repeat is ignored: a held key must not take the
         element and then, on the next repeat, the frame under it. The key is
         always claimed, deleted something or not: an unhandled Backspace is
         "back" to some engines (WebKitGTK, the Linux desktop app), and a
         canvas must never leave the page for it. */
      const canEdit = canEditOpenCanvas(useStore.getState())
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        if (canEdit && !e.repeat) deleteSelection()
      }
      /* Figma's walk of the selection: ↩ into it, ⇧↩ out to the parent, Tab
         and ⇧Tab along the siblings. Only the bare keys, and only when no
         control (a button, a menu, the Layers tree) has the keyboard — there
         ↩ presses and Tab moves focus, as they must. An unhandled key is left
         alone, so Tab still walks the chrome when nothing is selected. */
      const bare = !e.metaKey && !e.ctrlKey && !e.altKey
      if (bare && !presenting && (e.key === 'Enter' || e.key === 'Tab') && !isControlTarget(e.target)) {
        const moved =
          e.key === 'Enter' ? (e.shiftKey ? exitSelection() : enterSelection()) : selectSibling(e.shiftKey ? -1 : 1)
        if (moved) {
          e.preventDefault()
          return
        }
      }
      /* ⌘A: every frame on the page, or every sibling of the picked element */
      if (isSelectAllShortcut(e) && !presenting) {
        e.preventDefault()
        selectAll()
        return
      }
      /* ⌘] / ⌘[ and ⌘⌥] / ⌘⌥[ restack what is selected */
      const reorder = reorderShortcut(e)
      if (reorder && canEdit && !presenting) {
        e.preventDefault()
        reorderSelection(reorder)
        return
      }
      const nudge = ARROW_NUDGE[e.key]
      if (canEdit && nudge && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const step = e.shiftKey ? 10 : 1
        if (nudgeSelection(nudge[0] * step, nudge[1] * step)) e.preventDefault()
        return
      }
      if (e.key === 'Escape') {
        select(null)
        useStore.getState().setFollowing(null)
      }
      /* ⌘. hides every panel and toolbar; ⌘. again brings them back as they
         were. Escape deliberately leaves the UI hidden, as in Figma. */
      if (isHideUiShortcut(e) && hideUiActive) {
        e.preventDefault()
        toggleHideUi()
        return
      }
      /* ⌘⌥↩ presents from the selected frame (else the page's flow start),
         as the Play button does; while presenting the player owns the keys */
      if (isPresentShortcut(e) && !presenting) {
        e.preventDefault()
        const state = useStore.getState()
        const from = presentStartFrame(state.canvas, state.selectedId, state.activePageId)
        if (from) setPresenting(from)
        return
      }
      /* undo, redo, hide, lock and duplicate all change the canvas */
      if (canEdit && (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) void redo()
        else void undo()
        return
      }
      if (canEdit && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'y') {
        e.preventDefault()
        void redo()
        return
      }
      /* ⌘⇧H hides or shows what is selected — the picked element, else the
         frames — as the eye in the Layers panel would */
      if (canEdit && (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'h') {
        if (toggleSelectionHidden()) e.preventDefault()
        return
      }
      /* ⌘B / ⌘I / ⌘U, ⌘⇧X and ⌘⇧7 / ⌘⇧8 format a picked text layer whole, as in Figma */
      const textFormat = canEdit && (e.metaKey || e.ctrlKey) && !e.altKey ? layerTextShortcut(e) : null
      if (textFormat) {
        if (formatSelectionText(textFormat)) e.preventDefault()
        return
      }
      /* ⌘⇧L locks or unlocks what is selected: the picked layer, else the frames */
      if (canEdit && (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'l') {
        if (toggleLockSelection()) e.preventDefault()
        return
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        const state = useStore.getState()
        const picked = state.selectedElement
        const pickedFrame = state.canvas?.frames.find((f) => f.id === picked?.frameId)
        const frames = state.canvas?.frames.filter((f) => selectedIds.includes(f.id)) ?? []
        if (canEdit && e.key.toLowerCase() === 'd' && frames.length) {
          e.preventDefault()
          if (picked && pickedFrame) duplicateLayers(pickedFrame, pickedSelectors(state))
          else if (!picked) duplicateFrames(frames)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canvasId, hideUiActive, presenting]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Native copy/paste events let us write the selection's token and read
     clipboard images without asking for clipboard-read permission. */
  useEffect(() => {
    function onCopy(e: ClipboardEvent) {
      if (isTypingTarget(e.target) || window.getSelection()?.toString() || !e.clipboardData) return
      const state = useStore.getState()
      const picked = state.selectedElement
      const frame = state.canvas?.frames.find((f) => f.id === picked?.frameId)
      let copy: () => boolean | void
      if (picked) {
        if (!frame) return
        const selectors = pickedSelectors(state)
        copy = () => copyPicked(frame, selectors)
      } else {
        const frames = state.canvas?.frames.filter((f) => state.selectedIds.includes(f.id)) ?? []
        if (!frames.length) return
        copy = () => copyFrames(frames)
      }
      if (writeCanvasClipboard(copy, e.clipboardData)) e.preventDefault()
    }
    function onPaste(e: ClipboardEvent) {
      if (isTypingTarget(e.target)) return
      /* every paste lands a frame or a layer */
      if (!canEditOpenCanvas(useStore.getState())) return
      /* bitmaps land as pictures, SVGs as editable shapes and PDFs as pages, in one row */
      const incoming = splitIncoming([...(e.clipboardData?.files ?? [])], svgMarkupOf(e.clipboardData))
      if (hasIncoming(incoming)) {
        e.preventDefault()
        pasteFilesCentered(canvasId, incoming).catch((err: Error) => {
          setToast(translate('canvas.pasteImageFailed', { error: err.message }))
          window.setTimeout(() => setToast(null), 4000)
        })
      } else if (isCanvasClipboard(e.clipboardData) && hasElementClip()) {
        e.preventDefault()
        const state = useStore.getState()
        const picked = state.selectedElement
        const frame = state.canvas?.frames.find((f) => f.id === (picked?.frameId ?? state.selectedId))
        if (frame) pasteLayer(frame, picked?.selector ?? null)
      } else if (isCanvasClipboard(e.clipboardData) && hasFrameClip()) {
        e.preventDefault()
        pasteFrameCentered(canvasId)
      }
    }
    window.addEventListener('copy', onCopy)
    window.addEventListener('paste', onPaste)
    return () => {
      window.removeEventListener('copy', onCopy)
      window.removeEventListener('paste', onPaste)
    }
  }, [canvasId])

  /* Image, SVG and PDF files dragged in from the desktop land as frames
     centered on the drop point. Every file drag is claimed, so a drop the
     canvas cannot take (a viewer, a spreadsheet) is refused instead of the browser opening the file in
     place of the canvas; a drop the composer already took is left to it.
     While a file is over the page every frame keeps its shield up, or a
     frame being edited would swallow the drop in its iframe. Enter and leave
     fire for every element crossed, so a count tells when the drag is gone. */
  useEffect(() => {
    const carriesFiles = (e: DragEvent) => e.dataTransfer?.types.includes('Files') ?? false
    let depth = 0
    const setDragging = useStore.getState().setFileDragging
    function onDragEnter(e: DragEvent) {
      if (!carriesFiles(e)) return
      depth += 1
      setDragging(true)
    }
    function onDragLeave(e: DragEvent) {
      if (!carriesFiles(e)) return
      depth = Math.max(0, depth - 1)
      if (!depth) setDragging(false)
    }
    function endDrag() {
      depth = 0
      setDragging(false)
    }
    function onDragOver(e: DragEvent) {
      if (!carriesFiles(e) || e.defaultPrevented) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = canEditOpenCanvas(useStore.getState()) ? 'copy' : 'none'
    }
    function onDrop(e: DragEvent) {
      endDrag()
      if (!carriesFiles(e) || e.defaultPrevented) return
      e.preventDefault()
      if (!canEditOpenCanvas(useStore.getState())) return
      const incoming = splitIncoming([...(e.dataTransfer?.files ?? [])])
      if (!hasIncoming(incoming)) return
      dropFiles(canvasId, incoming, { x: e.clientX, y: e.clientY }).catch((err: Error) => {
        setToast(translate('canvas.dropImageFailed', { error: err.message }))
        window.setTimeout(() => setToast(null), 4000)
      })
    }
    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('drop', onDrop)
    window.addEventListener('dragend', endDrag)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('drop', onDrop)
      window.removeEventListener('dragend', endDrag)
      endDrag()
    }
  }, [canvasId])

  function showToast(msg: string) {
    setToast(msg)
    window.setTimeout(() => setToast(null), 2000)
  }

  /* ⌘. and View > Show/Hide UI: the first hide in a browser says how to undo it */
  function toggleHideUi() {
    useStore.getState().toggleUiHidden()
    if (useStore.getState().uiHidden && !hideUiHinted()) showToast(translate('canvas.uiHidden', { key: MOD_KEY }))
  }

  /* Export lists the page's frames, with the canvas selection pre-checked */
  function openExport() {
    const s = useStore.getState()
    if (!s.canvas) return
    const frames = s.activePageId ? pageFrames(s.canvas, s.activePageId) : s.canvas.frames
    setExporting({ frames, selected: s.selectedIds.filter((id) => frames.some((f) => f.id === id)) })
  }

  /* a pending Memory suggestion gets its own toast beside the side panel;
     clicking it jumps to the Memory tab, ✕ mutes it for this session */
  const pendingProposal = useStore((s) => s.proposals.find((p) => p.status === 'pending'))
  const panelTab = useStore((s) => s.panelTab)
  const [mutedProposal, setMutedProposal] = useState<string | null>(null)

  /* a decision landing in Memory is invisible work — surface it as its own
     memory toast in the same top-right stack. Only decisions captured after
     this page loaded count, so the ws-init batch stays silent. The summarizer
     re-broadcasts the decision with a generalized summary a moment later; the
     upsert re-fires this effect and the toast text swaps in place. */
  const latestDecision = useStore((s) => s.decisions[0])
  const [decisionToast, setDecisionToast] = useState<string | null>(null)
  const loadedAt = useRef(0)
  const decisionToastTimer = useRef<number | null>(null)
  useEffect(() => {
    if (!loadedAt.current) loadedAt.current = Date.now()
    if (latestDecision && latestDecision.at > loadedAt.current) {
      const line = latestDecision.summary ?? latestDecision.text
      setDecisionToast(line.length > 64 ? line.slice(0, 61) + '…' : line)
      if (decisionToastTimer.current) window.clearTimeout(decisionToastTimer.current)
      decisionToastTimer.current = window.setTimeout(() => setDecisionToast(null), 6000)
    }
  }, [latestDecision])

  async function addFrame() {
    if (readOnly) return
    const n = (canvas?.frames.length ?? 0) + 1
    const frame = await createCenteredFrame(canvasId, `Frame ${n}`, STARTER_HTML)
    select(frame.id)
  }

  const me = getIdentity()
  /* the face's photo comes from the account, not the room: our own presence
     is never in the roster */
  const { data: session } = authClient.useSession()
  const others = useMemo(
    () => Object.values(presences).filter((p) => p.clientId !== me.clientId),
    [presences, me.clientId],
  )

  const selectedFrame = canvas?.frames.find((f) => f.id === selectedId) ?? null
  /* the panel only shows when a frame-name click (or deep link) opened it —
     selecting a frame by clicking its surface must not slide it in */
  const inspectorOpen = useStore((s) => s.inspectorOpen)
  /* a right-click that selected the frame keeps the Inspector out until the
     context menu closes — it would slide in right under the open menu */
  const deferPanel = useStore((s) => !!s.ctxMenu?.deferPanel)
  const layersOpen = useStore((s) => s.layersOpen)
  /* ⌘. — the canvas view with nothing over it; the board and PresentMode keep their chrome */
  const uiHidden = useStore((s) => s.uiHidden) && hideUiActive
  /* the element panel takes the frame inspector's spot while a Layers row
     has it open; it follows the element selection until it is closed */
  const selectedElement = useStore((s) => s.selectedElement)
  const elementPanelOpen = useStore((s) => s.elementPanelOpen)
  const panelElement = elementPanelOpen && selectedElement?.frameId === selectedFrame?.id ? selectedElement : null
  /* with nothing selected the slot is the page's own settings, once opened
     from the rail, until closed */
  const pagePanelOpen = useStore((s) => s.pagePanelOpen)
  const pageShown = !readOnly && !selectedFrame && pagePanelOpen && !deferPanel
  /* the frame Inspector, the element panel or the Page panel, whichever has the slot */
  const propertiesShown =
    (!readOnly && !!selectedFrame && !deferPanel && (!!panelElement || inspectorOpen)) || pageShown
  /* ⇧R docks the rulers on the canvas's top and left edges; the bar and the
     floating chrome step in past them (the same test Stage draws them by) */
  const rulers = useStore((s) => s.rulers)
  const chromeHidden = useStore((s) => s.uiHidden)
  const rulersShown = rulers && !chromeHidden && !isMobile
  /* docked panels: the stage sits between the columns, under a solid bar */
  const docked = usePanelLayout() === 'docked' && !isMobile
  const panelSurface = docked ? 'docked' : 'floating'
  /* docked, the rulers sit on the stage's own edges inside the columns, so
     nothing steps in past them */
  const rulerInset = rulersShown && !docked ? RULER_PX : 0
  const hasSelection = useStore((s) => s.selectedIds.length > 0)

  const fixedChrome = chromeLayout({
    layersOpen,
    propertiesShown,
    agentsOpen: showActivity,
    layersWidth: 0,
    agentsWidth: 0,
    docked,
  })
  const panels = fitPanelWidths({
    saved: panelWidths,
    viewport: viewportWidth,
    /* docked, the panels squeeze the stage itself, so it keeps a usable width */
    reserved: fixedChrome.left + fixedChrome.right + rulerInset + (docked ? DOCKED_STAGE_MIN : 0),
    layersOpen,
    agentsOpen: showActivity,
  })
  const layersWidth = panels.layers
  const agentsWidth = panels.agents
  const chrome = chromeLayout({
    layersOpen,
    propertiesShown,
    agentsOpen: showActivity,
    layersWidth,
    agentsWidth,
    docked,
  })

  /* the desktop shell's View menu: its toggles act here, and it mirrors
     their state; leaving the canvas disables its canvas items */
  useEffect(
    () =>
      onViewCommand((command) => {
        const s = useStore.getState()
        if (command === 'rulers') s.toggleRulers()
        else if (command === 'ui' && hideUiActive) toggleHideUi()
        else if (command === 'layers') s.setLayersOpen(!s.layersOpen)
        else if (command === 'agents') setShowActivity((open) => !open)
      }),
    [hideUiActive], // eslint-disable-line react-hooks/exhaustive-deps
  )
  useEffect(() => {
    syncViewMenu({
      canvas: true,
      rulers,
      ui: !uiHidden,
      uiToggle: hideUiActive,
      layers: layersOpen,
      agents: showActivity,
      selection: hasSelection,
    })
  }, [rulers, uiHidden, hideUiActive, layersOpen, showActivity, hasSelection])
  useEffect(() => () => syncViewMenu(NO_CANVAS), [])
  const chromeVars = {
    '--chrome-top': isMobile ? '0px' : `${(docked ? DOCKED_BAR_H : BAR_H) + rulerInset}px`,
    '--rail-left': `${chrome.toolRail + rulerInset}px`,
    /* what floats over the canvas (the zoom pill, toasts) keeps clear of a
       docked column's edge */
    '--chrome-left': `${chrome.left + (docked ? FLOAT_GAP : 0)}px`,
    '--chrome-right': `${chrome.right + (docked ? FLOAT_GAP : 0)}px`,
  } as CSSProperties
  /* what the bar and the floating chrome cover of the stage, which spans the
     whole window; phones open their panels as sheets over everything */
  const stageInsets: StageInsets =
    isMobile || uiHidden || docked
      ? NO_INSETS
      : { top: BAR_H + rulerInset, right: chrome.right, bottom: 0, left: chrome.left + rulerInset }
  /* docked, the stage is the gap between the bar and the columns; ⌘. gives
     it the whole window back */
  const stageBounds: StageInsets | undefined =
    docked && !uiHidden ? { top: DOCKED_BAR_H, right: chrome.right, bottom: 0, left: chrome.left } : undefined
  const meFace = {
    name: me.name,
    image: avatarUrl(session?.user.image),
    color: presences[me.clientId]?.color ?? 'var(--brand)',
  }

  function openConnect() {
    posthog.capture('agent_connection_opened')
    setShowConnect(true)
  }

  /* the id in the URL isn't a canvas — a typo, a stale link, or one someone
     just deleted. Say so plainly rather than opening a canvas shell that
     can't load anything and never will. */
  if (canvasNotFound) {
    return (
      <AuthScreen>
        <AuthCard title={t('canvas.notFoundTitle')} align="center">
          <AuthCardText>{t('canvas.notFoundBody', { id: canvasId })}</AuthCardText>
          <Button className="self-center" onClick={() => navigate('/')}>
            {t('canvas.backToCanvases')}
          </Button>
        </AuthCard>
      </AuthScreen>
    )
  }

  return (
    /* --app-inset is 0 normally; the impersonation shell raises it so this
       fixed layer starts below the banner instead of under it */
    <div className="fixed inset-x-0 bottom-0 top-[var(--app-inset,0px)] bg-paper">
      {/* Floating pills over the canvas, which shows through the transparent
          bar: the title pill on the left, live presence centred, sharing on
          the right. Tablet (xs..md) folds the text actions into the •••
          sheet; phone (< xs) wraps to two rows. ⌘. hides the chrome (this
          bar, the panels, the toolbars) with display:none rather than
          unmounting it, so what it holds — a composer draft, the expanded
          layers, a scroll position — is exactly as it was when ⌘. brings it
          back. */}
      {/* with rulers on, the top ruler takes the window's top edge and the
          bar's pills float below it */}
      <TopBar
        variant={docked ? 'docked' : 'canvas'}
        className={cn(uiHidden && 'hidden')}
        style={rulerInset ? { top: rulerInset, left: rulerInset } : undefined}
      >
        <div className="flex min-w-0 items-center gap-2 max-xs:basis-full">
          <BarPill className="min-w-0 gap-1 pr-3 pl-1">
            <Tooltip label={t('canvas.allCanvases')} side="bottom" align="start">
              <Button
                variant="bare"
                size="icon-sm"
                className={barPillIconBtn}
                aria-label={t('canvas.allCanvases')}
                onClick={() => navigate('/')}
              >
                <FolderIcon className="size-3.5" />
              </Button>
            </Tooltip>
            <CanvasName readOnly={readOnly} />
            {readOnly && (
              <Badge tone="outline" title={t('canvas.readOnlyBadgeTitle')}>
                {role === 'comment' ? t('canvas.canComment') : t('canvas.viewOnly')}
              </Badge>
            )}
            <CanvasIdMenu canvasId={canvasId} onCopied={showToast} />
            {connected ? (
              <span className="ml-1 whitespace-nowrap text-ink-faint max-lg:hidden">{t('canvas.autoSaved')}</span>
            ) : (
              <span className="ml-1 whitespace-nowrap text-accent-ink">{t('canvas.reconnecting')}</span>
            )}
          </BarPill>
        </div>
        <div className={cn('absolute left-1/2 -translate-x-1/2 max-[1180px]:hidden', docked ? 'top-1' : 'top-3.5')}>
          <LivePresence me={meFace} others={others} onRenameSelf={() => setRenaming(true)} />
        </div>
        <div className="ml-auto flex flex-none items-center gap-2 max-md:hidden">
          {/* the presence pill needs the bar's centre; narrower desktops keep
              the plain avatar stack (and its follow buttons) over here */}
          <div
            className="mr-1 flex items-center min-[1181px]:hidden"
            title={others.map((p) => p.name).join(', ') || t('canvas.justYou')}
          >
            <PresenceFace name={meFace.name} image={meFace.image} color={meFace.color} kind="user" />
            <span className="-ml-[7px] flex items-center">
              <PeerAvatars others={others} />
            </span>
          </div>
          <BarPill>
            {!readOnly && (
              <>
                <Button
                  variant="bare"
                  className={barPillBtn}
                  onClick={() => setShowImport(true)}
                  title={t('canvas.importTitle')}
                >
                  <ImportIcon className="size-3.5" />
                  {t('canvas.import')}
                </Button>
                <BarDivider className="mx-0.5" />
              </>
            )}
            <Button variant="bare" className={barPillBtn} onClick={openExport} title={t('canvas.exportTitle')}>
              <ExportIcon className="size-3.5" />
              {t('canvas.export')}
            </Button>
          </BarPill>
          <BarPill>
            <Button variant="bare" className={barPillBtn} onClick={() => setShowShare(true)}>
              <ShareUpIcon className="size-3.5" />
              {t('canvas.share')}
            </Button>
            <Tooltip
              label={
                selectedId
                  ? `${t('canvas.presentFromFrame')} · ${PRESENT_SHORTCUT_HINT}`
                  : playFrom
                    ? `${t('canvas.presentPrototype')} · ${PRESENT_SHORTCUT_HINT}`
                    : t('canvas.addFrameToPresent')
              }
              side="bottom"
            >
              <span className="inline-flex">
                <Button
                  variant="bare"
                  size="icon-sm"
                  className={barPillIconBtn}
                  aria-label={t('canvas.presentPrototypeLabel')}
                  disabled={!playFrom}
                  onClick={() => setPresenting(playFrom)}
                >
                  <PlayIcon className="size-3.5" />
                </Button>
              </span>
            </Tooltip>
          </BarPill>
          {/* the bar's one black action — it opens the MCP connection modal */}
          <Button
            variant="primary"
            className={cn(docked ? barPrimaryCompactBtn : barPrimaryBtn, 'shadow-ring')}
            onClick={openConnect}
          >
            <PlusIcon className="size-3.5" strokeWidth={1.4} />
            {t('canvas.connectAi')}
          </Button>
        </div>
        <div className="ml-auto hidden items-center gap-1.5 max-md:flex max-xs:order-3">
          <div
            className="mr-1 flex items-center max-sm:hidden"
            title={others.map((p) => p.name).join(', ') || t('canvas.justYou')}
          >
            <PresenceFace name={meFace.name} image={meFace.image} color={meFace.color} kind="user" />
            <span className="-ml-[7px] flex items-center">
              <PeerAvatars others={others} />
            </span>
          </div>
          <Button
            variant="primary"
            className={cn(docked ? barPrimaryCompactBtn : barPrimaryBtn, 'shadow-ring')}
            onClick={openConnect}
          >
            <PlusIcon className="size-3.5" strokeWidth={1.4} />
            <span className="max-sm:hidden">{t('canvas.connectAi')}</span>
            <span className="sm:hidden">{t('canvas.connectAiShort')}</span>
          </Button>
          <Tooltip label={t('canvas.canvasActions')} side="bottom" align="end">
            <Button
              variant="ghost"
              size="icon"
              className={barIconBtn}
              aria-label={t('canvas.canvasActions')}
              onClick={() => setShowMobileActions(true)}
            >
              <MoreHorizontalIcon className="size-4" />
            </Button>
          </Tooltip>
        </div>
      </TopBar>

      {/* the canvas runs under the transparent top bar to the window's top
          edge; the floating chrome gets its own layer that starts below the
          bar, so every panel offset stays measured from there */}
      <div className="absolute inset-0 overflow-hidden" style={chromeVars}>
        <Stage onAddFrame={addFrame} insets={stageInsets} bounds={stageBounds} docked={docked} />
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 top-[50px] max-md:top-14 max-xs:top-[100px]',
            uiHidden && 'hidden',
          )}
          /* panel offsets are measured from this layer, so moving it past
             the rulers moves every panel with it */
          style={rulerInset || docked ? { top: 'var(--chrome-top)', left: rulerInset } : undefined}
        >
          <div className="pointer-events-none absolute top-3.5 right-[var(--chrome-right)] z-30 flex flex-col items-end gap-2 transition-[right] duration-150 [&>*]:pointer-events-auto max-md:top-[56px] max-md:right-2 max-md:left-2">
            {pendingProposal && mutedProposal !== pendingProposal.id && !(showActivity && panelTab === 'memory') && (
              <div className="flex items-center rounded-[10px] bg-surface shadow-ring">
                <Button
                  variant="bare"
                  className="h-8 pr-1 pl-3 text-[12px] font-medium text-ink hover:bg-transparent hover:text-ink"
                  onClick={() => {
                    useStore.getState().setPanelTab('memory')
                    setShowActivity(true)
                  }}
                >
                  <DoopMark size={12} /> {t('canvas.memorySuggestionReview')}
                </Button>
                <Button
                  variant="bare"
                  size="icon-sm"
                  className="mr-1 size-6 rounded-[6px] text-ink-faint"
                  title={t('canvas.hideForNow')}
                  aria-label={t('canvas.hideForNow')}
                  onClick={() => setMutedProposal(pendingProposal.id)}
                >
                  <XIcon className="size-3" />
                </Button>
              </div>
            )}
            {decisionToast && (
              <Button
                variant="bare"
                className="max-w-full items-center gap-2.5 whitespace-normal rounded-[12px] bg-surface px-3.5 py-2.5 text-left font-normal shadow-pop transition-shadow hover:bg-surface hover:shadow-ring-hover sm:max-w-[320px] [&_svg]:text-ink-soft"
                title={t('canvas.openMemory')}
                onClick={() => {
                  useStore.getState().setPanelTab('memory')
                  setShowActivity(true)
                  setDecisionToast(null)
                }}
              >
                <BrainIcon className="size-4" />
                <span>
                  <b className="block text-[12.5px] font-medium text-ink">{t('canvas.savedToMemory')}</b>
                  <span className="mt-[1px] block text-[12px] leading-[1.4] text-ink-soft">{decisionToast}</span>
                </span>
              </Button>
            )}
          </div>
          {/* the presence pill in the bar already carries each agent's status
              line; this per-agent stack is only for windows too narrow for it */}
          <WorkingNow style={{ left: chrome.left }} className="min-[1180px]:hidden" />
          <Onboarding canvasId={canvasId} />
          {!isMobile &&
            (layersOpen ? (
              <LayersPanel
                onAddFrame={addFrame}
                surface={panelSurface}
                resize={{
                  label: t('canvas.resizeLayers'),
                  edge: 'right',
                  width: layersWidth,
                  min: LAYERS_MIN,
                  max: panels.layersMax,
                  /* the other panel keeps the width it shows, not a larger saved
                     one the window had squeezed */
                  onResize: (layers) =>
                    setPanelWidths((widths) => ({ layers, agents: showActivity ? panels.agents : widths.agents })),
                }}
              />
            ) : (
              <LayersRailToggle docked={docked} />
            ))}
          {isMobile && !showActivity && (
            <MobilePanelTabs
              onOpen={(tab) => {
                useStore.getState().setPanelTab(tab)
                setShowActivity(true)
              }}
            />
          )}
          {!isMobile && !readOnly && selectedFrame && panelElement && !deferPanel && (
            <ElementPanel
              /* keyed by frame, not element: picking the next element swaps
                 the values in place instead of remounting the panel blank */
              key={selectedFrame.id}
              frame={selectedFrame}
              selector={panelElement.selector}
              surface={panelSurface}
              style={{ right: chrome.properties }}
            />
          )}
          {!isMobile && !readOnly && selectedFrame && inspectorOpen && !panelElement && !deferPanel && (
            <Inspector frame={selectedFrame} surface={panelSurface} style={{ right: chrome.properties }} />
          )}
          {!isMobile && pageShown && <PagePanel surface={panelSurface} style={{ right: chrome.properties }} />}
          {!isMobile && showActivity && (
            <ActivityPanel
              canvasId={canvasId}
              onClose={() => setShowActivity(false)}
              surface={panelSurface}
              style={{ right: docked ? 0 : EDGE }}
              resize={{
                label: t('canvas.resizeAgents'),
                edge: 'left',
                width: agentsWidth,
                min: AGENTS_MIN,
                max: panels.agentsMax,
                onResize: (agents) =>
                  setPanelWidths((widths) => ({ agents, layers: layersOpen ? panels.layers : widths.layers })),
              }}
            />
          )}
          {!isMobile && (
            <SideRail
              style={{ right: chrome.island }}
              docked={docked}
              agentsOpen={showActivity}
              onToggleAgents={() => setShowActivity((open) => !open)}
              inspectorOpen={propertiesShown}
              inspectorAvailable={!readOnly}
              inspectTarget={selectedFrame ? 'frame' : 'page'}
              onToggleInspector={() => {
                const s = useStore.getState()
                if (!selectedFrame) {
                  s.setPagePanelOpen(!s.pagePanelOpen)
                  return
                }
                if (propertiesShown) {
                  /* the element panel and the inspector share the slot —
                     hiding it closes whichever is showing */
                  s.setElementPanelOpen(false)
                  s.setInspectorOpen(false)
                } else s.setInspectorOpen(true)
              }}
              me={meFace}
              onRenameSelf={() => setRenaming(true)}
            />
          )}
        </div>
      </div>

      {isMobile && (
        <>
          <Sheet open={showMobileActions} onOpenChange={setShowMobileActions}>
            <SheetContent
              side="bottom"
              className="gap-0 rounded-t-2xl border-line bg-surface p-0 pb-[env(safe-area-inset-bottom)] shadow-pop"
            >
              <div className="border-b border-line-soft px-5 py-4 pr-14">
                <SheetTitle className="font-display text-lg font-extrabold">{t('canvas.canvasActions')}</SheetTitle>
                <SheetDescription className="mt-1 text-xs text-ink-soft">
                  {t('canvas.canvasActionsDescription')}
                </SheetDescription>
              </div>
              {/* the same glyphs as the desktop bar and islands; pages live
                  in the Layers sheet, as on desktop */}
              <div className="grid gap-2 p-4 [&_svg]:size-3.5 [&_svg]:text-ink-soft">
                <Button
                  variant="ghost"
                  className="h-11 justify-start gap-2.5 border-line bg-surface px-4"
                  onClick={() => {
                    setShowMobileActions(false)
                    setShowMobileLayers(true)
                  }}
                >
                  <LayersIcon /> {t('canvas.layers')}
                </Button>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    className="h-11 justify-start gap-2.5 border-line bg-surface px-4"
                    onClick={() => {
                      setShowMobileActions(false)
                      setShowImport(true)
                    }}
                  >
                    <ImportIcon /> {t('canvas.importWebsite')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="h-11 justify-start gap-2.5 border-line bg-surface px-4"
                  onClick={() => {
                    setShowMobileActions(false)
                    setShowShare(true)
                  }}
                >
                  <ShareIcon /> {t('canvas.shareCanvas')}
                </Button>
                {/* the phone's tool strip has no room for the Edit / Prototype
                    switch, so Prototype mode toggles here */}
                <Button
                  variant="ghost"
                  className="h-11 justify-start gap-2.5 border-line bg-surface px-4 aria-pressed:bg-paper-deep"
                  aria-pressed={prototypeMode}
                  onClick={() => {
                    setShowMobileActions(false)
                    useStore.getState().setPrototypeMode(!prototypeMode)
                  }}
                >
                  <PrototypeModeIcon /> {prototypeMode ? t('canvas.leavePrototypeMode') : t('canvas.prototypeMode')}
                </Button>
                <Button
                  variant="ghost"
                  className="h-11 justify-start gap-2.5 px-4 text-ink-soft"
                  onClick={() => navigate('/settings')}
                >
                  <GearIcon /> {t('shell.settings')}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <Sheet open={showMobileLayers && !uiHidden} onOpenChange={setShowMobileLayers}>
            <SheetContent
              side="bottom"
              showCloseButton={false}
              /* the first focusable is the panel's close button, whose
                 tooltip would pop on the sheet's auto-focus; the finger is
                 the focus on a phone anyway */
              onOpenAutoFocus={(e) => e.preventDefault()}
              className="max-h-[calc(100svh-56px)] gap-0 rounded-t-2xl border-line bg-surface p-0 shadow-pop data-[side=bottom]:h-[min(78svh,680px)]"
            >
              <SheetTitle className="sr-only">{t('canvas.layers')}</SheetTitle>
              <LayersPanel onAddFrame={addFrame} surface="inline" onClose={() => setShowMobileLayers(false)} />
            </SheetContent>
          </Sheet>
          {/* ⌘. closes both sheets (and their backdrops) for the moment only:
              the open state they read from is untouched, so they are back
              as they were when ⌘. restores the chrome */}
          <Sheet
            open={!readOnly && !!selectedFrame && inspectorOpen && !deferPanel && !uiHidden}
            onOpenChange={(open) => {
              if (!open) select(null)
            }}
          >
            <SheetContent
              side="bottom"
              showCloseButton={false}
              className="max-h-[calc(100svh-56px)] gap-0 rounded-t-2xl border-line bg-surface p-0 shadow-pop data-[side=bottom]:h-[min(78svh,680px)]"
            >
              <SheetTitle className="sr-only">{t('canvas.frameInspector')}</SheetTitle>
              {selectedFrame && <Inspector frame={selectedFrame} surface="inline" />}
            </SheetContent>
          </Sheet>
          <Sheet open={showActivity && !uiHidden} onOpenChange={setShowActivity}>
            <SheetContent
              side="bottom"
              showCloseButton={false}
              className="max-h-[calc(100svh-56px)] gap-0 rounded-t-2xl border-line bg-surface p-0 shadow-pop data-[side=bottom]:h-[min(78svh,680px)]"
            >
              <SheetTitle className="sr-only">{t('canvas.canvasActivity')}</SheetTitle>
              <ActivityPanel canvasId={canvasId} onClose={() => setShowActivity(false)} surface="inline" />
            </SheetContent>
          </Sheet>
        </>
      )}

      {renaming && <RenameSelfModal current={me.name} onClose={() => setRenaming(false)} />}
      {showConnect && <ConnectModal canvasId={canvasId} onClose={() => setShowConnect(false)} />}
      {presenting && <PresentMode frameId={presenting} onClose={() => setPresenting(null)} />}
      {showShare && canvas && (
        <ShareModal
          key={canvas.id}
          canvas={canvas}
          onChange={(patch) => {
            const current = useStore.getState().canvas
            if (current?.id === canvasId) useStore.getState().setCanvas({ ...current, ...patch })
          }}
          onClose={() => setShowShare(false)}
          onCopied={() => {
            setShowShare(false)
            showToast(t('canvas.linkCopied'))
          }}
        />
      )}
      {limitWall && (
        <LimitWall
          canvasId={canvasId}
          onClose={() => useStore.getState().setLimitWall(false)}
          onOpenConnect={() => {
            useStore.getState().setLimitWall(false)
            posthog.capture('agent_connection_opened')
            setShowConnect(true)
          }}
        />
      )}
      {exporting && (
        <ExportModal
          frames={exporting.frames}
          initialSelection={exporting.selected}
          onClose={() => setExporting(null)}
        />
      )}
      {showImport && (
        <ImportModal
          canvasId={canvasId}
          installPass={ghInstallPass}
          installError={ghInstallError}
          initialSource={importRequested && importRequested !== '1' ? importRequested : null}
          onClose={() => {
            setShowImport(false)
            setGhInstallPass(null)
          }}
          onDone={(frameIds, failedCount) => {
            setShowImport(false)
            select(frameIds[0] ?? null)
            const imported = t('canvas.itemsImported', { count: frameIds.length })
            showToast(failedCount ? t('canvas.importedWithFailures', { imported, failed: failedCount }) : imported)
          }}
          onQueued={(cardCount) => {
            setShowImport(false)
            setGhInstallPass(null)
            showToast(t('canvas.cardsQueued', { count: cardCount }))
          }}
        />
      )}
      {updateReady ? (
        <Toast>
          {t('canvas.appUpdated')}
          <ToastAction onClick={() => location.reload()}>{t('canvas.reload')}</ToastAction>
        </Toast>
      ) : toast ? (
        <Toast>{toast}</Toast>
      ) : (
        storeNotice && (
          <Toast>
            {storeNotice.busy && <ToastSpinner />}
            {storeNotice.text}
          </Toast>
        )
      )}
    </div>
  )
}

function ImportModal({
  canvasId,
  installPass,
  installError,
  initialSource = null,
  onClose,
  onDone,
  onQueued,
}: {
  canvasId: string
  installPass: string | null
  installError: string | null
  /** open straight on this source's screen (Home's picker already chose) */
  initialSource?: string | null
  onClose: () => void
  onDone: (frameIds: string[], failedCount: number) => void
  /** a repo import queues cards on the board instead of landing frames */
  onQueued: (cardCount: number) => void
}) {
  /* which source's screen is open — null is the picker; landing back from
     GitHub's install flow jumps straight to that screen */
  const t = useT()
  const [source, setSource] = useState<string | null>(installPass || installError ? 'github' : initialSource)
  const [url, setUrl] = useState('')
  const [wholeSite, setWholeSite] = useState(false)
  const [discovery, setDiscovery] = useState<DiscoveredSite | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<'discovering' | 'importing' | null>(null)
  const [error, setError] = useState<string | null>(null)
  /* a connected repo's screen manifest under review — the modal's third view */
  const [repoReview, setRepoReview] = useState<{ connection: GithubConnectionInfo; manifest: RepoManifest } | null>(
    null,
  )
  const [repoSelected, setRepoSelected] = useState<Set<string>>(new Set())
  const [extractSystem, setExtractSystem] = useState(true)
  const [showPages, setShowPages] = useState(false)

  function errorMessage(caught: unknown, fallback: string) {
    if (caught instanceof ApiError) return String(caught.body.error ?? fallback)
    return caught instanceof Error ? caught.message.replace(/^\d+\s*/, '') : fallback
  }

  function normalizedUrl() {
    const clean = url.trim()
    return /^https?:\/\//i.test(clean) ? clean : `https://${clean}`
  }

  async function runSinglePage() {
    if (!url.trim() || busy) return
    setBusy('importing')
    setError(null)
    try {
      const frame = await api.importPage(canvasId, normalizedUrl())
      posthog.capture('page_imported')
      onDone([frame.id], 0)
    } catch (e) {
      setError(errorMessage(e, t('canvas.errImportFailed')))
      setBusy(null)
    }
  }

  async function discover() {
    if (!url.trim() || busy) return
    setBusy('discovering')
    setError(null)
    try {
      const found = await api.discoverSitePages(canvasId, normalizedUrl())
      setDiscovery(found)
      setSelected(new Set(found.pages.map((page) => page.url)))
      posthog.capture('site_pages_discovered', { page_count: found.pages.length, truncated: found.truncated })
      setBusy(null)
    } catch (e) {
      setError(errorMessage(e, t('canvas.errDiscoveryFailed')))
      setBusy(null)
    }
  }

  async function importSelected() {
    if (!discovery || !selected.size || busy) return
    setBusy('importing')
    setError(null)
    const urls = discovery.pages.filter((page) => selected.has(page.url)).map((page) => page.url)
    try {
      const result = await api.importSitePages(canvasId, urls)
      if (!result.frames.length) {
        const reason = result.failures[0]?.error
        setError(reason ? t('canvas.errNoPagesReason', { reason }) : t('canvas.errNoPages'))
        setBusy(null)
        return
      }
      posthog.capture('website_imported', {
        requested_count: urls.length,
        imported_count: result.frames.length,
        failed_count: result.failures.length,
      })
      onDone(
        result.frames.map((frame) => frame.id),
        result.failures.length,
      )
    } catch (e) {
      setError(errorMessage(e, t('canvas.errWebsiteImportFailed')))
      setBusy(null)
    }
  }

  function togglePage(pageUrl: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(pageUrl)) next.delete(pageUrl)
      else next.add(pageUrl)
      return next
    })
  }

  /* screens can share a route across kinds (a page and its committed dist
     HTML) — key rows by kind + route */
  const screenKey = (s: RepoScreen) => `${s.kind}|${s.route}`

  function openRepoReview(connection: GithubConnectionInfo, manifest: RepoManifest) {
    setRepoReview({ connection, manifest })
    /* the design system is the default import; components and pages are
       one click away, never silently pre-committed */
    setRepoSelected(new Set())
    setShowPages(false)
    setError(null)
  }

  function toggleScreen(key: string) {
    setRepoSelected((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function importRepoScreens() {
    if (!repoReview || busy || (!repoSelected.size && !extractSystem)) return
    setBusy('importing')
    setError(null)
    const screens = repoReview.manifest.screens.filter((s) => repoSelected.has(screenKey(s)))
    try {
      const result = await api.importGithubScreens(canvasId, repoReview.connection.id, screens, extractSystem)
      if (!result.cards.length) {
        setError(result.rejected.length ? t('canvas.errScreensGone') : t('canvas.errAlreadyOnBoard'))
        setBusy(null)
        return
      }
      posthog.capture('github_screens_imported', {
        requested_count: screens.length,
        queued_count: result.cards.length,
        rejected_count: result.rejected.length,
      })
      onQueued(result.cards.length)
    } catch (e) {
      if (isResidentLimit(e)) {
        useStore.getState().setLimitWall(true)
        onClose()
        return
      }
      setError(errorMessage(e, t('canvas.errRepoImportFailed')))
      setBusy(null)
    }
  }

  const selectedCount = selected.size
  const laneLabel: Record<RepoScreen['source'], string> = {
    static: t('canvas.laneStatic'),
    placeholder: t('canvas.lanePlaceholder'),
  }
  const kindLabel = (s: RepoScreen) =>
    s.kind === 'component'
      ? t('canvas.kindComponent')
      : s.kind === 'story'
        ? t('canvas.kindStory')
        : laneLabel[s.source]
  const KIND_ORDER: Record<RepoScreen['kind'], number> = { component: 0, story: 1, static: 2, page: 3 }
  const allScreens = repoReview?.manifest.screens ?? []
  const pageCount = allScreens.filter((s) => s.kind === 'page').length
  const visibleScreens = allScreens
    .filter((s) => showPages || s.kind !== 'page')
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind])
  /* the import button names what goes: "Import design system + 3 components" */
  const repoCount = repoSelected.size
  const pickedOnlyComponents =
    repoCount === 1 ||
    visibleScreens
      .filter((s) => repoSelected.has(screenKey(s)))
      .every((s) => s.kind === 'component' || s.kind === 'story')
  const repoImportItems = [
    extractSystem ? t('canvas.repoPieceSystem') : '',
    repoCount
      ? pickedOnlyComponents
        ? t('canvas.repoPieceComponents', { count: repoCount })
        : t('canvas.repoPieceScreens', { count: repoCount })
      : '',
  ]
    .filter(Boolean)
    .join(' + ')

  return (
    <Modal size="xl" className="sm:max-w-[820px]" onClose={() => !busy && onClose()}>
      <>
        {repoReview ? (
          <>
            <div className="flex flex-col items-start justify-between gap-2.5 sm:flex-row sm:gap-6">
              <div className="flex flex-col gap-[5px]">
                <ModalEyebrow>{t('canvas.reviewBeforeImport')}</ModalEyebrow>
                <ModalTitle>{t('canvas.repoTitle')}</ModalTitle>
              </div>
              <Badge shape="pill">
                {repoReview.connection.repo}@{repoReview.connection.branch}
              </Badge>
            </div>
            <ModalLede>
              {repoReview.manifest.framework
                ? `${t('canvas.repoFramework', { framework: repoReview.manifest.framework })} `
                : ''}
              {t('canvas.repoLede')}
            </ModalLede>
            <CheckboxCard
              checked={extractSystem}
              disabled={!!busy}
              onChange={setExtractSystem}
              title={t('canvas.extractTitle')}
              description={t('canvas.extractDescription')}
            />
            <SelectionBar
              className="mt-4"
              count={repoSelected.size}
              total={visibleScreens.length}
              actions={
                <>
                  <SelectionBarAction
                    disabled={!!busy}
                    onClick={() => setRepoSelected(new Set(visibleScreens.map(screenKey)))}
                  >
                    {t('canvas.selectAll')}
                  </SelectionBarAction>
                  <SelectionBarAction disabled={!!busy} onClick={() => setRepoSelected(new Set())}>
                    {t('canvas.clear')}
                  </SelectionBarAction>
                </>
              }
            />
            <div
              className={cn(
                'max-h-[calc(100dvh-390px)] overflow-y-auto rounded-[11px] border border-line bg-paper transition-opacity sm:max-h-[min(350px,calc(100vh-390px))]',
                busy && 'opacity-[0.58]',
              )}
              role="group"
              aria-label={t('canvas.screensToImport')}
            >
              {visibleScreens.map((screen, index) => (
                <label
                  className="relative grid min-h-[58px] cursor-pointer grid-cols-[20px_24px_minmax(0,1fr)_auto] items-center gap-2.5 border-b border-line bg-surface px-3 py-[9px] first:rounded-t-[10px] last:rounded-t-none last:rounded-b-[10px] last:border-b-0 hover:bg-paper"
                  key={screenKey(screen)}
                >
                  <Checkbox
                    checked={repoSelected.has(screenKey(screen))}
                    disabled={!!busy}
                    onChange={() => toggleScreen(screenKey(screen))}
                  />
                  <span className="font-mono text-[9.5px] text-ink-faint">{String(index + 1).padStart(2, '0')}</span>
                  <span className="min-w-0">
                    <b className="block overflow-hidden whitespace-nowrap text-ellipsis text-[12.5px] text-ink">
                      {screen.title}
                    </b>
                    <span className="mt-[3px] block overflow-hidden whitespace-nowrap text-ellipsis font-mono text-[10px] text-ink-faint">
                      {screen.route}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-[3px] text-micro font-medium',
                      screen.source === 'placeholder'
                        ? 'bg-paper-deep text-ink-faint'
                        : 'bg-accent-ink/10 text-accent-ink',
                    )}
                  >
                    {kindLabel(screen)}
                  </span>
                </label>
              ))}
            </div>
            {pageCount > 0 && (
              <Button
                variant="bare"
                size="inline"
                className="mt-2 self-start text-ink-faint"
                disabled={!!busy}
                onClick={() => setShowPages((v) => !v)}
              >
                {showPages ? t('canvas.hidePages') : t('canvas.showPages', { count: pageCount })}
              </Button>
            )}
            {repoReview.manifest.truncated && <p className={importNoteCls}>{t('canvas.repoTruncated')}</p>}
            {busy === 'importing' && (
              <p className={cn(importNoteCls, 'text-accent-ink')}>{t('canvas.repoImporting')}</p>
            )}
            {error && <p className={errorNoteCls}>{error}</p>}
            <ModalActions className="justify-between">
              <Button
                variant="ghost"
                disabled={!!busy}
                onClick={() => {
                  setRepoReview(null)
                  setError(null)
                }}
              >
                <ArrowLeftIcon size={14} />
                {t('common.back')}
              </Button>
              <Button
                variant="primary"
                disabled={!!busy || (!repoSelected.size && !extractSystem)}
                onClick={importRepoScreens}
              >
                {busy === 'importing'
                  ? t('canvas.importing')
                  : repoImportItems && t('canvas.repoImport', { items: repoImportItems })}
              </Button>
            </ModalActions>
          </>
        ) : !discovery ? (
          <>
            {source === null ? (
              <SourcePicker onPick={setSource} sources={importSources()} />
            ) : source === 'website' ? (
              <>
                <ImportStepHeader
                  icon={<WebIcon className="size-[22px] text-ink" />}
                  title={t('canvas.websiteTitle')}
                />
                <ModalLede>{t('canvas.websiteLede')}</ModalLede>
                <Field className="mt-[22px]" label={t('canvas.websiteUrl')} labelVariant="form" htmlFor="import-url">
                  <Input
                    id="import-url"
                    variant="well"
                    inputSize="lg"
                    className="font-mono"
                    autoFocus
                    placeholder="https://example.com"
                    value={url}
                    disabled={!!busy}
                    onChange={(e) => {
                      setUrl(e.target.value)
                      setError(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (wholeSite) void discover()
                        else void runSinglePage()
                      }
                    }}
                  />
                </Field>
                <CheckboxCard
                  checked={wholeSite}
                  disabled={!!busy}
                  onChange={(next) => {
                    setWholeSite(next)
                    setError(null)
                  }}
                  title={
                    <>
                      {t('canvas.wholeSiteTitle')}{' '}
                      <em className="ml-[7px] rounded-full bg-paper-deep px-1.5 py-px text-micro font-medium not-italic text-ink-faint">
                        {t('canvas.optional')}
                      </em>
                    </>
                  }
                  description={t('canvas.wholeSiteDescription')}
                />
                {error && <p className={errorNoteCls}>{error}</p>}
                <ImportStepFooter note={t('canvas.websiteNote')} onBack={() => setSource(null)} backDisabled={!!busy}>
                  <Button
                    variant="primary"
                    disabled={!!busy || !url.trim()}
                    onClick={wholeSite ? discover : runSinglePage}
                  >
                    {busy === 'discovering'
                      ? t('canvas.findingPages')
                      : busy === 'importing'
                        ? t('canvas.importing')
                        : wholeSite
                          ? t('canvas.discoverPages')
                          : t('canvas.importPage')}
                  </Button>
                </ImportStepFooter>
              </>
            ) : source === 'liveapp' ? (
              <SyncKeysSection canvasId={canvasId} onBack={() => setSource(null)} />
            ) : source === 'github' ? (
              <GithubSection
                canvasId={canvasId}
                installPass={installPass}
                installError={installError}
                onReview={openRepoReview}
                onBack={() => setSource(null)}
              />
            ) : null}
          </>
        ) : (
          <>
            <div className="flex flex-col items-start justify-between gap-2.5 sm:flex-row sm:gap-6">
              <div className="flex flex-col gap-[5px]">
                <ModalEyebrow>{t('canvas.reviewBeforeImport')}</ModalEyebrow>
                <ModalTitle>{t('canvas.choosePages')}</ModalTitle>
              </div>
              <Badge shape="pill">{new URL(discovery.siteUrl).hostname}</Badge>
            </div>
            <ModalLede>{t('canvas.pagesFound', { count: discovery.pages.length })}</ModalLede>
            <SelectionBar
              className="mt-5"
              count={selectedCount}
              total={discovery.pages.length}
              actions={
                <>
                  <SelectionBarAction
                    disabled={!!busy}
                    onClick={() => setSelected(new Set(discovery.pages.map((p) => p.url)))}
                  >
                    {t('canvas.selectAll')}
                  </SelectionBarAction>
                  <SelectionBarAction disabled={!!busy} onClick={() => setSelected(new Set())}>
                    {t('canvas.clear')}
                  </SelectionBarAction>
                </>
              }
            />
            <div
              className={cn(
                'max-h-[calc(100dvh-390px)] overflow-y-auto rounded-[11px] border border-line bg-paper transition-opacity sm:max-h-[min(350px,calc(100vh-390px))]',
                busy && 'opacity-[0.58]',
              )}
              role="group"
              aria-label={t('canvas.pagesToImport')}
            >
              {discovery.pages.map((page, index) => {
                const pageUrl = new URL(page.url)
                let pathname = pageUrl.pathname
                try {
                  pathname = decodeURIComponent(pathname)
                } catch {
                  /* Keep the encoded path when a site contains a malformed escape. */
                }
                const path = pathname + pageUrl.search
                return (
                  <label
                    className="relative grid min-h-[58px] cursor-pointer grid-cols-[20px_24px_minmax(0,1fr)] items-center gap-2.5 border-b border-line bg-surface px-3 py-[9px] first:rounded-t-[10px] last:rounded-t-none last:rounded-b-[10px] last:border-b-0 hover:bg-paper"
                    key={page.url}
                  >
                    <Checkbox
                      checked={selected.has(page.url)}
                      disabled={!!busy}
                      onChange={() => togglePage(page.url)}
                    />
                    <span className="font-mono text-[9.5px] text-ink-faint">{String(index + 1).padStart(2, '0')}</span>
                    <span className="min-w-0">
                      <b className="block overflow-hidden whitespace-nowrap text-ellipsis text-[12.5px] text-ink">
                        {page.title}
                      </b>
                      <span className="mt-[3px] block overflow-hidden whitespace-nowrap text-ellipsis font-mono text-[10px] text-ink-faint">
                        {path || '/'}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
            {discovery.truncated && <p className={importNoteCls}>{t('canvas.pagesTruncated')}</p>}
            {busy === 'importing' && (
              <p className={cn(importNoteCls, 'text-accent-ink')}>
                {t('canvas.capturingPages', { count: selectedCount })}
              </p>
            )}
            {error && <p className={errorNoteCls}>{error}</p>}
            <ModalActions className="justify-between">
              <Button
                variant="ghost"
                disabled={!!busy}
                onClick={() => {
                  setDiscovery(null)
                  setError(null)
                }}
              >
                <ArrowLeftIcon size={14} />
                {t('common.back')}
              </Button>
              <Button variant="primary" disabled={!!busy || !selectedCount} onClick={importSelected}>
                {busy === 'importing'
                  ? t('canvas.importingCount', { count: selectedCount })
                  : t('canvas.importPages', { count: selectedCount })}
              </Button>
            </ModalActions>
          </>
        )}
      </>
    </Modal>
  )
}

/** Your display name, the identity on your cursor and in the feed. It lives on
 *  the account, so saving it updates the account and rejoins the canvas. */
function RenameSelfModal({ current, onClose }: { current: string; onClose: () => void }) {
  const t = useT()
  const [draft, setDraft] = useState(current)
  const [busy, setBusy] = useState(false)
  const clean = draft.trim()

  function save() {
    if (!clean || clean === current || busy) return onClose()
    setBusy(true)
    authClient.updateUser({ name: clean }).then(
      () => {
        setName(clean)
        location.reload()
      },
      (err: unknown) => {
        console.error(err)
        setBusy(false)
      },
    )
  }

  return (
    <Modal size="sm" onClose={onClose}>
      <>
        <ModalTitle>{t('canvas.displayNameTitle')}</ModalTitle>
        <ModalLede>{t('canvas.displayNameLede')}</ModalLede>
        <Field className="mt-5" label={t('canvas.name')} labelVariant="form" htmlFor="display-name">
          <Input
            id="display-name"
            autoFocus
            value={draft}
            maxLength={60}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
        </Field>
        <ModalActions>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" disabled={busy || !clean || clean === current} onClick={save}>
            {busy ? t('canvas.saving') : t('canvas.saveName')}
          </Button>
        </ModalActions>
      </>
    </Modal>
  )
}

/* Design sync: mint write-only snippet keys so an app pushes its live screens
   onto this canvas — the import path for products behind SSO/VPN where the
   server-side importer can't go. */

function SyncKeysSection({ canvasId, onBack }: { canvasId: string; onBack: () => void }) {
  const t = useT()
  const [keys, setKeys] = useState<SyncKeyInfo[] | null>(null)
  const [name, setAppName] = useState('')
  const [busy, setBusy] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  /* keys are owner/member-only (durable access) — link-edit visitors get a
     403 and shouldn't see the section at all */
  const [forbidden, setForbidden] = useState(false)

  useEffect(() => {
    api
      .listSyncKeys(canvasId)
      .then(setKeys)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setForbidden(true)
        setKeys([])
      })
  }, [canvasId])

  /* key in the src query string — the one attribute tag managers never strip */
  const snippetFor = (secret: string) =>
    `<script async src="${location.origin}/doop-sync.js?key=${secret}"></` + `script>`

  async function create() {
    if (busy || !name.trim()) return
    setBusy(true)
    try {
      const key = await api.createSyncKey(canvasId, name.trim())
      setKeys((k) => [key, ...(k ?? [])])
      setAppName('')
      posthog.capture('sync_key_created')
    } catch (e) {
      console.error(e)
    }
    setBusy(false)
  }

  function revoke(keyId: string) {
    api.deleteSyncKey(canvasId, keyId).catch(console.error)
    setKeys((k) => k?.filter((x) => x.id !== keyId) ?? null)
  }

  async function copySnippet(key: SyncKeyInfo) {
    await navigator.clipboard.writeText(snippetFor(key.secret))
    setCopiedId(key.id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  if (forbidden) {
    return (
      <>
        <ImportStepHeader icon={<RadioTowerIcon className="size-[22px] text-ink" />} title={t('canvas.syncTitle')} />
        <ModalLede>{t('canvas.syncForbidden')}</ModalLede>
        <ImportStepFooter onBack={onBack} />
      </>
    )
  }
  return (
    <>
      <ImportStepHeader icon={<RadioTowerIcon className="size-[22px] text-ink" />} title={t('canvas.syncTitle')} />
      <ModalLede>{t('canvas.syncLede')}</ModalLede>
      {(keys ?? []).map((k) => (
        <div key={k.id} className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-[13px]">
            <b className="font-semibold">{k.name}</b>
            <Note className="mr-auto">
              {k.frames
                ? t('canvas.screens', { count: k.frames })
                : k.lastUsedAt
                  ? t('canvas.synced')
                  : t('canvas.neverSynced')}
            </Note>
            <Button
              variant="bare"
              size="icon-sm"
              className="-mr-1.5 hover:bg-accent-ink/10 hover:text-accent-ink"
              title={t('canvas.revokeKey')}
              aria-label={t('canvas.revokeKey')}
              onClick={() => revoke(k.id)}
            >
              <XIcon size={14} />
            </Button>
          </div>
          <div className="relative">
            <Textarea
              className="resize-none border-line-soft bg-ink/[0.04] py-2 pl-2.5 pr-[84px] font-mono text-[11px] leading-normal text-ink-faint focus:border-line focus:text-ink focus:ring-0 md:text-[11px] [word-break:break-all]"
              readOnly
              rows={4}
              value={snippetFor(k.secret)}
              onFocus={(e) => e.target.select()}
            />
            <Button
              size="sm"
              className="absolute top-3 right-3.5 gap-1.5 bg-surface px-2.5 text-xs"
              onClick={() => copySnippet(k)}
            >
              {copiedId === k.id ? (
                t('canvas.copiedBang')
              ) : (
                <>
                  <CopyIcon className="size-3.5" />
                  {t('common.copy')}
                </>
              )}
            </Button>
          </div>
        </div>
      ))}
      <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row">
        <Field className="flex-1" label={t('canvas.appName')} labelVariant="form" htmlFor="sync-app-name">
          <Input
            id="sync-app-name"
            variant="well"
            inputSize="lg"
            placeholder={t('canvas.appNamePlaceholder')}
            value={name}
            disabled={busy}
            onChange={(e) => setAppName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
          />
        </Field>
        <Button
          variant="primary"
          className="justify-center self-end sm:h-12"
          disabled={busy || !name.trim()}
          onClick={create}
        >
          {t('canvas.createKey')}
        </Button>
      </div>
      <ol className="mt-3 flex flex-col divide-y divide-line-soft rounded-[10px] border border-line">
        {[
          [t('canvas.syncStep1Title'), t('canvas.syncStep1Detail')],
          [t('canvas.syncStep2Title'), t('canvas.syncStep2Detail')],
          [t('canvas.syncStep3Title'), t('canvas.syncStep3Detail')],
        ].map(([title, detail], i) => (
          <li key={title} className="flex gap-3.5 px-3.5 py-3">
            <span className="font-mono text-[11px] leading-[1.6] text-ink-faint">0{i + 1}</span>
            <span className="min-w-0">
              <b className="block text-[13.5px] font-semibold tracking-[-0.012em]">{title}</b>
              <span className="text-[12.5px] leading-[1.45] text-ink-soft">{detail}</span>
            </span>
          </li>
        ))}
      </ol>
      <ImportStepFooter note={t('canvas.syncNote')} onBack={onBack} />
    </>
  )
}

/* GitHub as an import source: one click installs the doop GitHub App on the
   repos you pick and you land back here on a repo picker — no tokens to
   copy. Pasting a fine-grained PAT stays as the fallback when the app isn't
   configured (self-hosters) or someone prefers it. Credentials stay on the
   server either way — this section only ever sees connection metadata.
   Same durable-access rule as sync keys. */
function GithubSection({
  canvasId,
  installPass,
  installError,
  onReview,
  onBack,
}: {
  canvasId: string
  installPass: string | null
  installError: string | null
  onReview: (connection: GithubConnectionInfo, manifest: RepoManifest) => void
  onBack: () => void
}) {
  const t = useT()
  const [connections, setConnections] = useState<GithubConnectionInfo[] | null>(null)
  const [appEnabled, setAppEnabled] = useState(false)
  const [showTokenForm, setShowTokenForm] = useState(false)
  const [pickerRepos, setPickerRepos] = useState<InstallationRepo[] | null>(null)
  const [repo, setRepo] = useState('')
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState<'connecting' | string | null>(null)
  const [error, setError] = useState<string | null>(installError)
  const [forbidden, setForbidden] = useState(false)

  useEffect(() => {
    api
      .listGithubConnections(canvasId)
      .then(setConnections)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setForbidden(true)
        setConnections([])
      })
    api
      .githubAppInfo()
      .then((info) => setAppEnabled(info.enabled))
      .catch(() => setAppEnabled(false))
  }, [canvasId])

  /* back from GitHub: swap the pass for the repo list */
  useEffect(() => {
    if (!installPass) return
    api
      .listInstallationRepos(canvasId, installPass)
      .then(setPickerRepos)
      .catch((e) => failed(e, translate('canvas.errListRepos')))
  }, [canvasId, installPass])

  function failed(caught: unknown, fallback: string) {
    setError(caught instanceof ApiError ? String(caught.body.error ?? fallback) : fallback)
    setBusy(null)
  }

  async function startInstall() {
    if (busy) return
    setBusy('connecting')
    setError(null)
    try {
      const { url } = await api.startGithubInstall(canvasId)
      posthog.capture('github_app_install_started')
      location.href = url
    } catch (e) {
      failed(e, t('canvas.errStartInstall'))
    }
  }

  async function connectInstalledRepo(fullName: string) {
    if (busy || !installPass) return
    setBusy('connecting')
    setError(null)
    try {
      const conn = await api.connectGithub(canvasId, { repo: fullName, pass: installPass })
      setConnections((c) => [conn, ...(c ?? [])])
      setBusy(null)
      posthog.capture('github_repo_connected', { via: 'app' })
    } catch (e) {
      failed(e, t('canvas.errConnectRepo'))
    }
  }

  async function connectRepo() {
    if (busy || !repo.trim() || !token.trim()) return
    setBusy('connecting')
    setError(null)
    try {
      const conn = await api.connectGithub(canvasId, {
        repo: repo.trim(),
        token: token.trim(),
      })
      setConnections((c) => [conn, ...(c ?? [])])
      setRepo('')
      setToken('')
      setBusy(null)
      posthog.capture('github_repo_connected', { via: 'token' })
    } catch (e) {
      failed(e, t('canvas.errConnectRepo'))
    }
  }

  async function analyze(conn: GithubConnectionInfo) {
    if (busy) return
    setBusy(conn.id)
    setError(null)
    try {
      const manifest = await api.analyzeGithub(canvasId, conn.id)
      posthog.capture('github_screens_found', {
        screen_count: manifest.screens.length,
        framework: manifest.framework,
      })
      setBusy(null)
      onReview(conn, manifest)
    } catch (e) {
      failed(e, t('canvas.errAnalysis'))
    }
  }

  function disconnect(connId: string) {
    api.deleteGithubConnection(canvasId, connId).catch(console.error)
    setConnections((c) => c?.filter((x) => x.id !== connId) ?? null)
  }

  /* the picker offers what this canvas has not connected yet */
  const unconnectedRepos = pickerRepos?.filter(
    (r) => !connections?.some((conn) => conn.repo.toLowerCase() === r.fullName.toLowerCase()),
  )

  if (forbidden) {
    return (
      <>
        <ImportStepHeader icon={<GithubIcon width={22} height={22} />} title={t('canvas.githubTitle')} />
        <ModalLede>{t('canvas.githubForbidden')}</ModalLede>
        <ImportStepFooter onBack={onBack} />
      </>
    )
  }
  return (
    <>
      <ImportStepHeader icon={<GithubIcon width={22} height={22} />} title={t('canvas.githubTitle')} />
      <ModalLede>{t('canvas.githubLede')}</ModalLede>
      <div className="mt-[22px] flex flex-col gap-2.5">
        {(connections ?? []).map((conn) => (
          <div key={conn.id} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-[13px]">
              <b className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
                {conn.repo}
                <span className="font-normal text-ink-faint">@{conn.branch}</span>
              </b>
              <Note className="mr-auto shrink-0">
                {conn.frames ? t('canvas.screens', { count: conn.frames }) : t('canvas.nothingImported')}
              </Note>
              <Button size="sm" className="px-2.5 text-xs" disabled={!!busy} onClick={() => analyze(conn)}>
                {busy === conn.id ? t('canvas.analyzing') : t('canvas.analyze')}
              </Button>
              <Button
                variant="bare"
                size="icon-sm"
                className="-mr-1.5 hover:bg-accent-ink/10 hover:text-accent-ink"
                title={t('canvas.disconnectRepo')}
                aria-label={t('canvas.disconnectRepo')}
                onClick={() => disconnect(conn.id)}
              >
                <XIcon size={14} />
              </Button>
            </div>
          </div>
        ))}
        {unconnectedRepos && (
          <div className="flex flex-col gap-1.5 rounded-[11px] border border-line bg-paper p-2.5">
            <b className="text-[12px] text-ink-soft">{t('canvas.pickRepo')}</b>
            {unconnectedRepos.map((r) => (
              <div key={r.fullName} className="flex items-center gap-2 text-[13px]">
                <GithubIcon width={13} height={13} className="shrink-0 text-ink-faint" />
                <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[12px]">
                  {r.fullName}
                  {r.private && <span className="ml-1.5 text-[10px] text-ink-faint">{t('canvas.private')}</span>}
                </span>
                <Button
                  size="sm"
                  className="px-2.5 text-xs"
                  disabled={!!busy}
                  onClick={() => connectInstalledRepo(r.fullName)}
                >
                  {busy === 'connecting' ? t('canvas.connecting') : t('canvas.connect')}
                </Button>
              </div>
            ))}
            {!unconnectedRepos.length && <Note>{t('canvas.allConnected')}</Note>}
            <div className="flex items-center gap-3 pt-1">
              <Button variant="bare" size="inline" className="text-ink-faint" asChild>
                <a href={`/api/canvases/${canvasId}/github/app/install`} target="_blank" rel="noreferrer">
                  {t('canvas.addRepos')}
                </a>
              </Button>
              {/* asks GitHub again rather than re-reading the pass: an
                  installation made in the other tab is only in a new one */}
              <Button variant="bare" size="inline" className="text-ink-faint" disabled={!!busy} onClick={startInstall}>
                {t('canvas.refreshRepos')}
              </Button>
            </div>
          </div>
        )}
        {appEnabled && !pickerRepos && (
          <Button variant="primary" className="justify-center self-start" disabled={!!busy} onClick={startInstall}>
            {busy === 'connecting' ? t('canvas.openingGithub') : t('canvas.connectGithub')}
          </Button>
        )}
        {appEnabled && !showTokenForm && (
          <Button
            variant="bare"
            size="inline"
            className="self-start text-ink-faint"
            onClick={() => setShowTokenForm(true)}
          >
            {t('canvas.pasteToken')}
          </Button>
        )}
        {(!appEnabled || showTokenForm) && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col items-stretch gap-2 sm:flex-row">
              <Input
                variant="well"
                className="flex-1 font-mono text-[12px]"
                placeholder={t('canvas.repoPlaceholder')}
                value={repo}
                disabled={!!busy}
                onChange={(e) => {
                  setRepo(e.target.value)
                  setError(null)
                }}
              />
              <Input
                variant="well"
                className="flex-1 font-mono text-[12px]"
                type="password"
                placeholder={t('canvas.tokenPlaceholder')}
                value={token}
                disabled={!!busy}
                onChange={(e) => {
                  setToken(e.target.value)
                  setError(null)
                }}
                onKeyDown={(e) => e.key === 'Enter' && connectRepo()}
              />
            </div>
            <Button
              variant="primary"
              className="justify-center self-start"
              disabled={!!busy || !repo.trim() || !token.trim()}
              onClick={connectRepo}
            >
              {busy === 'connecting' ? t('canvas.connecting') : t('canvas.connect')}
            </Button>
          </div>
        )}
        {error && <p className={errorNoteCls}>{error}</p>}
      </div>
      <ImportStepFooter
        note={appEnabled ? t('canvas.githubNoteApp') : t('canvas.githubNoteToken')}
        onBack={onBack}
        backDisabled={!!busy}
      />
    </>
  )
}

/* The id badge in the bar opens a menu that copies the id (what agents hand
   the MCP tools) or the canvas link. */
function CanvasIdMenu({ canvasId, onCopied }: { canvasId: string; onCopied: (msg: string) => void }) {
  const t = useT()
  async function copy(text: string, msg: string) {
    try {
      await navigator.clipboard.writeText(text)
      onCopied(msg)
    } catch {
      onCopied(t('canvas.copyFailed'))
    }
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Pressable forwards the trigger's ref (Badge does not), so the menu
            anchors to the badge and focus returns to it on close */}
        <Pressable
          layout="inline"
          focus="ring"
          aria-label={t('canvas.canvasIdMenu')}
          title={t('canvas.canvasIdTitle')}
          className={cn(badgeVariants({ interactive: true }), 'max-md:hidden data-[state=open]:bg-current/10')}
        >
          {canvasId}
        </Pressable>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="start" width="sm">
        <DropdownMenuItem onSelect={() => void copy(canvasId, t('canvas.idCopied'))}>
          <CopyIcon size="rail" />
          {t('canvas.copyId')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void copy(`${location.origin}/c/${canvasId}`, t('canvas.linkCopied'))}>
          <LinkIcon size="rail" />
          {t('canvas.copyLink')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/* The canvas title doubles as its rename field. */
function CanvasName({ readOnly }: { readOnly: boolean }) {
  const canvas = useStore((s) => s.canvas)
  return (
    <TopBarTitle
      pill
      readOnly={readOnly}
      loading={!canvas}
      value={canvas?.name ?? ''}
      onCommit={(name) => {
        if (!canvas) return
        api.renameCanvas(canvas.id, name).catch(console.error)
        useStore.getState().renameCanvasLocal(name)
      }}
    />
  )
}
